import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Clock, MessageCircle, CheckCircle, Loader2, Globe } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Phone, label: "Phone", value: "+91 7289007800", href: "tel:+917289007800" },
    { icon: Mail, label: "Email", value: "hello@paxgrow.in", href: "mailto:hello@paxgrow.in" },
    { icon: MessageCircle, label: "WhatsApp", value: "+91 7289007800", href: "https://wa.me/917289007800" },
    { icon: MapPin, label: "Office", value: "M003 Basement Floor, Amrapali Sapphire, Sector 45, Noida 201301, UP, India", href: null },
    { icon: Clock, label: "Hours", value: "24/7 Available for International Patients", href: null },
    { icon: Globe, label: "Serving", value: "15+ Countries Worldwide", href: null },
  ];

  return (
    <div data-testid="contact-page">
      <section className="bg-gradient-to-b from-orange-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-orange-600/10 text-orange-600 border-orange-600/20 mb-4 font-body text-xs uppercase tracking-wider">Get In Touch</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-4" data-testid="contact-title">Contact Us</h1>
          <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto">Our medical tourism team is available 24/7 to help you plan your healthcare journey to India.</p>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <Card className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm" data-testid="contact-form-card">
                {success ? (
                  <div className="flex flex-col items-center py-10 gap-4">
                    <CheckCircle className="h-16 w-16 text-orange-600" />
                    <h3 className="font-heading text-2xl font-semibold">Message Sent!</h3>
                    <p className="text-slate-500 text-center">We'll get back to you within 24 hours.</p>
                    <Button onClick={() => setSuccess(false)} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full mt-4" data-testid="send-another-btn">Send Another Message</Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="grid gap-5" data-testid="contact-form">
                    <h2 className="font-heading text-2xl font-semibold text-slate-900">Send Us a Message</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="c-name">Full Name *</Label>
                        <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-name" />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="c-email">Email *</Label>
                        <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" data-testid="contact-email" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="c-phone">Phone *</Label>
                        <Input id="c-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 234 567 890" data-testid="contact-phone" />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="c-subject">Subject *</Label>
                        <Input id="c-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="How can we help?" data-testid="contact-subject" />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="c-message">Message *</Label>
                      <Textarea id="c-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your requirements..." rows={5} data-testid="contact-message" />
                    </div>
                    <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full font-semibold h-12 shadow-lg" data-testid="contact-submit-btn">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {loading ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                )}
              </Card>
            </div>

            <div className="lg:col-span-2 space-y-4">
              {contactInfo.map((c, i) => (
                <Card key={i} className="bg-white rounded-xl p-5 border border-slate-100 hover:border-emerald-300 transition-colors" data-testid={`contact-info-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-600/10 flex items-center justify-center shrink-0">
                      <c.icon className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-slate-700 text-sm font-medium hover:text-orange-600 transition-colors">
                          {c.value}
                        </a>
                      ) : (
                        <p className="text-slate-700 text-sm font-medium">{c.value}</p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
